import React, { useState } from 'react';
import { Calendar, Clock, MapPin, Filter, ChevronDown } from 'lucide-react';
import { format } from 'date-fns';
import Button from '../../../components/ui/Button';
import Select from '../../../components/ui/Select';

const OutageHistory = ({ provider, outages = [] }) => {
  const [filteredOutages, setFilteredOutages] = useState(outages);
  const [filters, setFilters] = useState({
    timeRange: 'all',
    severity: 'all',
    status: 'all'
  });
  const [showFilters, setShowFilters] = useState(false);

  const handleFilterChange = (filterType, value) => {
    const newFilters = { ...filters, [filterType]: value };
    setFilters(newFilters);
    applyFilters(newFilters);
  };

  const applyFilters = (currentFilters) => {
    let filtered = [...outages];

    // Time range filter
    if (currentFilters?.timeRange !== 'all') {
      const days = {
        '7d': 7,
        '30d': 30,
        '90d': 90
      };
      
      const cutoffDate = new Date();
      cutoffDate?.setDate(cutoffDate?.getDate() - days?.[currentFilters?.timeRange]);
      
      filtered = filtered?.filter(outage => 
        new Date(outage?.created_at) >= cutoffDate
      );
    }

    // Severity filter
    if (currentFilters?.severity !== 'all') {
      filtered = filtered?.filter(outage => outage?.severity === currentFilters?.severity);
    }

    // Status filter
    if (currentFilters?.status !== 'all') {
      filtered = filtered?.filter(outage => outage?.status === currentFilters?.status);
    }

    setFilteredOutages(filtered);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'resolved': return 'text-green-600 bg-green-100';
      case 'investigating': return 'text-yellow-600 bg-yellow-100';
      case 'identified': return 'text-orange-600 bg-orange-100';
      case 'monitoring': return 'text-blue-600 bg-blue-100';
      default: return 'text-red-600 bg-red-100';
    }
  };

  const getSeverityColor = (severity) => {
    switch (severity) {
      case 'low': return 'text-green-600 bg-green-100';
      case 'medium': return 'text-yellow-600 bg-yellow-100';
      case 'high': return 'text-orange-600 bg-orange-100';
      case 'critical': return 'text-red-600 bg-red-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  const timeRangeOptions = [
    { value: 'all', label: 'All Time' },
    { value: '7d', label: 'Last 7 Days' },
    { value: '30d', label: 'Last 30 Days' },
    { value: '90d', label: 'Last 90 Days' }
  ];

  const severityOptions = [
    { value: 'all', label: 'All Severities' },
    { value: 'low', label: 'Low' },
    { value: 'medium', label: 'Medium' },
    { value: 'high', label: 'High' },
    { value: 'critical', label: 'Critical' }
  ];

  const statusOptions = [
    { value: 'all', label: 'All Statuses' },
    { value: 'reported', label: 'Reported' },
    { value: 'investigating', label: 'Investigating' },
    { value: 'identified', label: 'Identified' },
    { value: 'monitoring', label: 'Monitoring' },
    { value: 'resolved', label: 'Resolved' }
  ];

  return (
    <div className="bg-white rounded-lg shadow-sm">
      {/* Header and Filters */}
      <div className="p-6 border-b border-gray-200">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
          <h3 className="text-lg font-semibold text-gray-900 mb-4 sm:mb-0">
            Outage History ({filteredOutages?.length})
          </h3>
          
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowFilters(!showFilters)}
            className="flex items-center"
          >
            <Filter className="h-4 w-4 mr-2" />
            Filters
            <ChevronDown className={`h-4 w-4 ml-2 transform transition-transform ${showFilters ? 'rotate-180' : ''}`} />
          </Button>
        </div>

        {/* Filter Controls */}
        {showFilters && (
          <div className="mt-4 grid grid-cols-1 sm:grid-cols-3 gap-4">
            <Select
              value={filters?.timeRange}
              onValueChange={(value) => handleFilterChange('timeRange', value)}
              placeholder="Select time range"
            >
              {timeRangeOptions?.map((option) => (
                <option key={option?.value} value={option?.value}>
                  {option?.label}
                </option>
              ))}
            </Select>

            <Select
              value={filters?.severity}
              onValueChange={(value) => handleFilterChange('severity', value)}
              placeholder="Select severity"
            >
              {severityOptions?.map((option) => (
                <option key={option?.value} value={option?.value}>
                  {option?.label}
                </option>
              ))}
            </Select>

            <Select
              value={filters?.status}
              onValueChange={(value) => handleFilterChange('status', value)}
              placeholder="Select status"
            >
              {statusOptions?.map((option) => (
                <option key={option?.value} value={option?.value}>
                  {option?.label}
                </option>
              ))}
            </Select>
          </div>
        )}
      </div>

      {/* Outage List */}
      <div className="divide-y divide-gray-200">
        {filteredOutages?.length > 0 ? (
          filteredOutages?.map((outage) => (
            <div key={outage?.id} className="p-6 hover:bg-gray-50">
              <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between">
                <div className="flex-1">
                  <h4 className="text-lg font-medium text-gray-900 mb-2">
                    {outage?.title}
                  </h4>
                  
                  <p className="text-gray-600 mb-3 leading-relaxed">
                    {outage?.description}
                  </p>

                  <div className="flex flex-wrap items-center gap-4">
                    <span className={`inline-flex px-3 py-1 rounded-full text-sm font-medium capitalize ${getStatusColor(outage?.status)}`}>
                      {outage?.status}
                    </span>
                    
                    <span className={`inline-flex px-3 py-1 rounded-full text-sm font-medium capitalize ${getSeverityColor(outage?.severity)}`}>
                      {outage?.severity}
                    </span>

                    <div className="flex items-center text-sm text-gray-500">
                      <Calendar className="h-4 w-4 mr-1" />
                      {format(new Date(outage?.created_at), 'MMM dd, yyyy')}
                    </div>

                    <div className="flex items-center text-sm text-gray-500">
                      <Clock className="h-4 w-4 mr-1" />
                      {format(new Date(outage?.created_at), 'HH:mm')}
                    </div>

                    {outage?.affected_areas && (
                      <div className="flex items-center text-sm text-gray-500">
                        <MapPin className="h-4 w-4 mr-1" />
                        {outage?.affected_areas}
                      </div>
                    )}
                  </div>
                </div>

                <div className="mt-4 lg:mt-0 lg:ml-6">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => window.location.href = `/service-outage-details?id=${outage?.id}`}
                  >
                    View Details
                  </Button>
                </div>
              </div>
            </div>
          ))
        ) : (
          <div className="p-12 text-center">
            <Calendar className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No outages found</h3>
            <p className="text-gray-500">
              {outages?.length === 0 
                ? `${provider?.name} has no reported outages.`
                : 'No outages match your current filters.'
              }
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default OutageHistory;